<template>
  <WizardContent
    v-if="loading"
    :show-sidebar="false"
    class="wizard-wrapper"
  >
    <div class="ma-auto">
      <v-progress-circular
        indeterminate
        color="brandPrimary"
        size="40"
      />
    </div>
  </WizardContent>
  <WizardContent
    v-else
    :show-sidebar="false"
    class="wizard-wrapper"
  >
    <template #flashMessage>
      <EduWarningFlashMessage :email="email" />
    </template>
    <GateHeader>
      <template #logo>
        <img
          v-if="iconUrl"
          :src="iconUrl"
          :alt="`${projectName} logo`"
          class="company-logo"
        />
        <JobNavIcon v-else />
      </template>
      <template #page-title>
        Welcome to JobNav!
      </template>
      <template #below-title>
        Join the {{ projectName }} team on JobNav.<br />
        Already have an account?
        <router-link
          to="login"
        >
          Sign in
        </router-link>
      </template>
    </GateHeader>
    <div v-if="!ssoMandatory">
      <v-form
        ref="form"
        v-model="valid"
        @submit.prevent="submitForm"
      >
        <div
          class="flex-grow-1 d-flex align-center justify-space-between"
        >
          <DpplSignupFormHalfTextField
            v-model="firstName"
            label="First Name"
            class="form-text-field"
            has-details
            :rules="firstNameRules"
            validate-on="blur"
          />
          <DpplSignupFormHalfTextField
            v-model="lastName"
            label="Last Name"
            class="form-text-field"
            has-details
            :rules="lastNameRules"
            validate-on="blur"
            margin="0px 0px 0px 24px"
          />
        </div>
        <DpplTextField
          v-model="email"
          label="Email"
          :rules="emailRules"
          class="form-text-field"
          has-details
          validate-on="blur"
        />
        <DpplTextField
          v-model="password"
          class="mb-6"
          label="Password"
          :rules="passwordRules"
          type="password"
          autocomplete="off"
          validate-on="blur"
          data-sentry-mask
        />
        <DpplSignupFormButton
          class="mb-4"
          type="submit"
          block
          :loading="loading"
        >
          Continue
        </DpplSignupFormButton>
      </v-form>
    </div>
    <section
      aria-label="Oauth and SSO logins"
      class="signup-btns"
    >
      <GoogleButton
        v-if="!ssoMandatory || ssoIsGoogle"
        :google-url="googleUrl"
      />
      <MicrosoftButton
        v-if="!ssoMandatory || ssoIsMicrosoft"
        :microsoft-url="microsoftUrl"
      />
      <SsoLoginButton
        v-if="ssoName && !(ssoIsGoogle || ssoIsMicrosoft)"
        class="w-100"
        :sso-provider="projectName"
        :sso-name="ssoName"
        :is-google="false"
        :is-microsoft="false"
      />
    </section>
    <p class="text-body-3 text-textDark pt-6">
      <a
        href="https://hello.datapeople.io/docs/tos"
        target="_blank"
        rel="noopener noreferrer"
        class="text-decoration-underline text-linkDefault"
      >
        Terms of Service
      </a>
      &nbsp;and
      <a
        href="https://www.payscale.com/about/privacy-policy"
        target="_blank"
        rel="noopener noreferrer"
        class="text-decoration-underline text-linkDefault"
      >
        Privacy Policy
      </a>
    </p>
  </WizardContent>
</template>

<script>
import { mapActions } from 'pinia';
import WizardContent from '@/components/shared/WizardContent.vue';
import GateHeader from '@/components/shared/gates/GateHeader.vue';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplSignupFormButton from '@/components/datapeople/DpplSignupFormButton.vue';
import DpplSignupFormHalfTextField from '@/components/datapeople/DpplSignupFormHalfTextField.vue';
import EduWarningFlashMessage from '@/components/shared/EduWarningFlashMessage.vue';
import GoogleButton from '@/components/shared/gates/auth/GoogleButton.vue';
import MicrosoftButton from '@/components/shared/gates/auth/MicrosoftButton.vue';
import SsoLoginButton from '@/components/signup/SsoLoginButton.vue';
import {
  signupBuildAuthUrls, signupGetProjectNameAndImage, signupJoinDashboard,
} from '@/api/signup';
import validation from '@/helpers/validation';
import { useWizard } from '@/stores/wizard/wizard';
import JobNavIcon from '@/assets/jobnav-payscale-icon-logo.svg?component';

export default {
  name: 'JoinDashboard',
  components: {
    WizardContent,
    DpplTextField,
    DpplSignupFormButton,
    DpplSignupFormHalfTextField,
    EduWarningFlashMessage,
    GateHeader,
    JobNavIcon,
    GoogleButton,
    MicrosoftButton,
    SsoLoginButton,
  },
  emits: ['track-page-load'],
  data() {
    return {
      projectName: '',
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      firstNameRules: validation.firstNameRules(),
      lastNameRules: validation.lastNameRules(),
      emailRules: validation.emailRules(),
      passwordRules: validation.passwordRules(),
      inviteKey: this.$route.query.key,
      valid: false,
      loading: false,
      googleUrl: null,
      microsoftUrl: null,
      iconUrl: null,
      ssoName: '',
      ssoMandatory: false,
      ssoIsGoogle: false,
      ssoIsMicrosoft: false,
    };
  },
  head() {
    return {
      title: this.$route.meta.title || 'JobNav',
      meta: [
        {
          name: 'description',
          content: 'Want to try the JobNav Smart Editor for Job Descriptions for free? Create'
          + ' a new JobNav account to begin editing your company’s job descriptions.',
        },
      ],
    };
  },
  async created() {
    if (!this.inviteKey) {
      this.$router.push({ name: 'login' });
    }

    const getLinkedinAndGoogleOauthUrls = async () => {
      const responseJSON = await signupBuildAuthUrls({ inviteKey: this.inviteKey });
      if (responseJSON.success === true) {
        this.googleUrl = responseJSON.google_auth_url;
        this.microsoftUrl = responseJSON.microsoft_auth_url;
      }
    };

    const getProjectNameAndImage = async () => {
      const responseJSON = await signupGetProjectNameAndImage({ key: this.inviteKey });
      this.iconUrl = responseJSON.imageUrl;
      this.projectName = responseJSON.projectName;
      this.ssoName = responseJSON.ssoName || '';
      this.ssoMandatory = responseJSON.ssoMandatory;
      this.ssoIsGoogle = responseJSON.ssoIsGoogle;
      this.ssoIsMicrosoft = responseJSON.ssoIsMicrosoft;
    };

    try {
      this.loading = true;

      await Promise.all([
        getProjectNameAndImage().catch(() => {
          this.$router.push({ name: 'login' });
        }),
        getLinkedinAndGoogleOauthUrls().catch((e) => {
          this.updateFlash({ message: e.message });
        }),
      ]);
    } finally {
      this.loading = false;
    }
  },
  beforeCreate() {
    this.$emit('track-page-load', { page: 'Join Dashboard' });
  },
  methods: {
    ...mapActions(useWizard, ['updateFlash']),
    async submitForm() {
      try {
        const formValidateResult = await this.$refs.form.validate();
        if (formValidateResult.valid) {
          this.loading = true;
          const responseJSON = await signupJoinDashboard({
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email,
            password: this.password,
            inviteKey: this.inviteKey,
          });
          if (responseJSON.success === true) {
            this.$router.push({
              name: 'terms-of-service',
            });
          }
        }
      } catch (e) {
        if (!e.message || e.message.charAt(0) !== '{') {
          this.updateFlash({ message: `Join error: ${e.message}` });
          throw e;
        }
        const responseJSON = JSON.parse(e.message);
        if (responseJSON.errors && responseJSON.errors.sso_mandatory) {
          if (responseJSON.errors.is_google) {
            window.location.href = '/signup/google';
          } else if (responseJSON.errors.is_microsoft) {
            window.location.href = '/signup/microsoft';
          } else {
            window.location.href = `/login/${responseJSON.errors.provider}`;
          }
          return;
        }
        if (
          responseJSON.errors
          && responseJSON.errors.auth
          && responseJSON.errors.auth.includes('Email does not match invite key.')
        ) {
          this.updateFlash({ message: 'You must use your company email address to register.' });
        } else {
          this.updateFlash({ message: 'Something went wrong' });
        }
        throw e;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="less">
.wizard-wrapper {
  min-width: 330px;
  width: 100%;
  align-items: center;
  justify-content: center;
}

:deep(.wizard-flash-message) {
  border-top-right-radius: 0px !important;
}

:deep(.slot-flash-message.full-width) {
  width: 450px;
}

.company-logo {
  max-width: 60px;
  max-height: 60px;
}

.signup-btns {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  gap: 16px;
}

.form-text-field :deep(.v-field .v-field__outline__start),
.form-text-field :deep(.v-field .v-field__outline__end) {
  border: 1px solid @grey-400;
}
</style>
